import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '../../molecules/ConfirmationDialog/ConfirmationDialog';
import FinancialAdvisorTable from './FinancialAdvisorTable';
import { ADVISOR_ROUTES } from '../../../routes/routeConstants';
import { Box, styled } from '@mui/system';
import { getAdminUserRoles } from '../../../common/constants';
import { BasicButton, TextboxInput, Icon } from '../../atoms';
import { useTheme } from '@mui/material/styles';
import { SelectInput } from '../../molecules/AdminFilters/SelectInput';

const SearchContainer = styled(Box)(({ theme }: any) => ({
    minWidth: '340px',
    '& .MuiOutlinedInput-root': {
        height: '40px',
        borderRadius: '6px'
    },
    '& .MuiOutlinedInput-input': {
        paddingLeft: '10px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.semantic.bd.subtle
    }
}));

const StyledOuterGrid = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    // padding: '12px',
    width: '100%',
    // border: '1px solid #E6E6E6',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05)',
    borderRadius: '12px'
}));

const ButtonContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '8px',
    width: '100%',
    paddingRight: '8px'
}));

const ClearButton = styled('button')(() => ({
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: 0
}));
interface FinancialAdvisorProps {
    financialAdvisors: any;
    deleteModalState: boolean;
    columns: any;
    handleCloseDeleteModal: () => void;
    advisorSelected: any;
    handleRemoveFinancialAdvisor: () => void;
    userRole: any;
    userTenant: any;
    // eslint-disable-next-line no-unused-vars
    searchAdvisors: (query: string) => void;
    searchTerm: string;
    // eslint-disable-next-line no-unused-vars
    handleRoleFilterChange: (role: string) => void;
    filterRole: string;
    tableLoader?: boolean;
}

export default function FinancialAdvisor({
    financialAdvisors,
    deleteModalState,
    columns,
    handleCloseDeleteModal,
    advisorSelected,
    handleRemoveFinancialAdvisor,
    userRole,
    userTenant,
    searchAdvisors,
    searchTerm,
    filterRole,
    handleRoleFilterChange,
    tableLoader
}: FinancialAdvisorProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const admin_userRoles = getAdminUserRoles(t);
    const handleNewAdvisor = () => {
        navigate(ADVISOR_ROUTES.ADD_USER, { state: { fromAdvisor: true, userRole: userRole, userTenant: userTenant } });
    };

    let userRoleOptions = admin_userRoles.filter((item) => item.accessibleTo.includes(userRole));
    userRoleOptions.unshift({ label: t('ALL_ROLES'), value: 'allroles', accessibleTo: [] });

    return (
        <StyledOuterGrid>
            <Box
                sx={{
                    minHeight: '20px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    gap: 2,
                    alignItems: 'center',
                    mb: 2
                }}
            >
                <SelectInput
                    label={t('ROLE')}
                    name="filter-role"
                    value={filterRole}
                    options={userRoleOptions}
                    onChange={(value) => handleRoleFilterChange(value ?? '')}
                />
                <SearchContainer>
                    <TextboxInput
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <Icon id="search_ftds" size={16} fill={theme.palette.primary[500]} />
                            ),
                            endAdornment: searchTerm?.length ? (
                                <ClearButton type="button" onClick={() => searchAdvisors('')}>
                                    <Icon id="rounded-close" size={17} />
                                </ClearButton>
                            ) : null
                        }}
                        value={searchTerm}
                        placeholder={t('SEARCH_BY_NAME_EMAIL')}
                        onChange={(e) => searchAdvisors(e.target.value)}
                        autoComplete="off"
                    />
                </SearchContainer>
                <ButtonContainer>
                    <BasicButton variant="contained" type="submit" onClick={handleNewAdvisor}>
                        {t('FINANCIAL_ADVISORS_ADD_NEW_USER')}
                    </BasicButton>
                </ButtonContainer>
            </Box>

            <FinancialAdvisorTable data={financialAdvisors} columns={columns} tableLoader={tableLoader} />

            <ConfirmationDialog
                onCancellation={handleCloseDeleteModal}
                onCloseIconClick={handleCloseDeleteModal}
                onConfirmation={() => handleRemoveFinancialAdvisor()}
                open={deleteModalState}
                content={`${t('REMOVE_ADVISOR_MODAL_CONFIRMATION_TEXT', {
                    advisorName: advisorSelected.advisorName
                })}`}
            />
        </StyledOuterGrid>
    );
}
